import JSONModel from "sap/ui/model/json/JSONModel";
import Event from "sap/ui/base/Event";
import Input from "sap/m/Input";
import BaseController from "./BaseController";
import AuthService from "../service/AuthService";
import MessageBox from "sap/m/MessageBox";

/**
 * @namespace com.ang.portal.controller
 */
interface PasswordRules {
	ruleLength: boolean;
	ruleUpper: boolean;
	ruleLower: boolean;
	ruleNumber: boolean;
	ruleSymbol: boolean;
}

export default class ResetPassword extends BaseController {
	private static readonly ROUTE_ARGUMENTS_PARAM = "arguments" as const;

	private static evaluatePasswordRules(password: string): PasswordRules {
		return {
			ruleLength: password.length >= 8,
			ruleUpper: /[A-Z]/.test(password),
			ruleLower: /[a-z]/.test(password),
			ruleNumber: /[0-9]/.test(password),
			ruleSymbol: /[^A-Za-z0-9]/.test(password),
		};
	}

	public onInit(): void {
		const oModel = new JSONModel({
			token: "",
			password: "",
			confirmPassword: "",
			busy: false,
			errorMessage: "",
			passwordState: "None",
			passwordStateText: "",
			confirmState: "None",
			confirmStateText: "",
			ruleLength: false,
			ruleUpper: false,
			ruleLower: false,
			ruleNumber: false,
			ruleSymbol: false,
			passwordVisible: false,
			confirmVisible: false,
		});
		this.setModel(oModel, "resetPassword");

		this.getRouter()
			.getRoute("resetPassword")
			.attachPatternMatched((oEvent) => this.onRouteMatched(oEvent), this);
	}

	private onRouteMatched(
		oEvent: Event<{ arguments?: { token?: string } }>,
	): void {
		const oModel = this.getModel("resetPassword") as JSONModel;
		const token = oEvent.getParameter(
			ResetPassword.ROUTE_ARGUMENTS_PARAM,
		)?.token;
		oModel.setProperty("/token", token || "");
		oModel.setProperty("/password", "");
		oModel.setProperty("/confirmPassword", "");
		oModel.setProperty("/errorMessage", "");
		oModel.setProperty("/passwordState", "None");
		oModel.setProperty("/confirmState", "None");
		oModel.setProperty("/ruleLength", false);
		oModel.setProperty("/ruleUpper", false);
		oModel.setProperty("/ruleLower", false);
		oModel.setProperty("/ruleNumber", false);
		oModel.setProperty("/ruleSymbol", false);
		oModel.setProperty("/passwordVisible", false);
		oModel.setProperty("/confirmVisible", false);
	}

	public async onPasswordLiveChange(
		oEvent: Event<{ id?: string; value?: string }>,
	): Promise<void> {
		const oModel = this.getModel("resetPassword") as JSONModel;
		const liveValue = oEvent.getParameter("value") ?? "";
		const sourceId = (oEvent.getSource() as Input).getId();

		if (sourceId.endsWith("resetPasswordInput")) {
			oModel.setProperty("/password", liveValue);
		} else {
			oModel.setProperty("/confirmPassword", liveValue);
		}

		const password = (oModel.getProperty("/password") as string) || "";
		const confirmPassword =
			(oModel.getProperty("/confirmPassword") as string) || "";
		const bundle = await this.getResourceBundle();

		const rules = ResetPassword.evaluatePasswordRules(password);
		oModel.setProperty("/ruleLength", rules.ruleLength);
		oModel.setProperty("/ruleUpper", rules.ruleUpper);
		oModel.setProperty("/ruleLower", rules.ruleLower);
		oModel.setProperty("/ruleNumber", rules.ruleNumber);
		oModel.setProperty("/ruleSymbol", rules.ruleSymbol);

		const allRulesMet = Object.values(rules).every(Boolean);

		if (!password) {
			oModel.setProperty("/passwordState", "None");
		} else if (!allRulesMet) {
			oModel.setProperty("/passwordState", "Error");
			oModel.setProperty(
				"/passwordStateText",
				bundle.getText("resetPasswordErrorPasswordWeak"),
			);
		} else {
			oModel.setProperty("/passwordState", "Success");
		}

		if (!confirmPassword) {
			oModel.setProperty("/confirmState", "None");
		} else if (confirmPassword !== password) {
			oModel.setProperty("/confirmState", "Error");
			oModel.setProperty(
				"/confirmStateText",
				bundle.getText("resetPasswordErrorPasswordMismatch"),
			);
		} else {
			oModel.setProperty("/confirmState", "Success");
		}
	}

	public onTogglePasswordVisibility(oEvent: Event): void {
		const oModel = this.getModel("resetPassword") as JSONModel;
		const sourceId = (oEvent.getSource() as Input).getId();
		const property = sourceId.endsWith("resetPasswordInput")
			? "/passwordVisible"
			: "/confirmVisible";
		oModel.setProperty(property, !oModel.getProperty(property));
	}

	public async onResetPasswordPress(): Promise<void> {
		const oModel = this.getModel("resetPassword") as JSONModel;
		const token = (oModel.getProperty("/token") as string) || "";
		const password = (oModel.getProperty("/password") as string) || "";
		const confirmPassword =
			(oModel.getProperty("/confirmPassword") as string) || "";
		const bundle = await this.getResourceBundle();

		if (!token) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("resetPasswordErrorTokenMissing"),
			);
			return;
		}

		if (!password || !confirmPassword) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("resetPasswordErrorPasswordRequired"),
			);
			return;
		}

		const rules = ResetPassword.evaluatePasswordRules(password);
		if (!Object.values(rules).every(Boolean)) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("resetPasswordErrorPasswordWeak"),
			);
			return;
		}

		if (password !== confirmPassword) {
			oModel.setProperty(
				"/errorMessage",
				bundle.getText("resetPasswordErrorPasswordMismatch"),
			);
			return;
		}

		oModel.setProperty("/busy", true);
		oModel.setProperty("/errorMessage", "");

		try {
			await AuthService.resetPassword(token, password);
			MessageBox.success(bundle.getText("resetPasswordSuccess"), {
				title: bundle.getText("resetPasswordTitle"),
				onClose: () => this.navTo("login", {}, true),
			});
		} catch (error) {
			oModel.setProperty(
				"/errorMessage",
				error instanceof Error && error.message
					? error.message
					: bundle.getText("forgotPasswordErrorGeneric"),
			);
		} finally {
			oModel.setProperty("/busy", false);
		}
	}

	public onBackToLoginPress(): void {
		this.navTo("login", {}, true);
	}
}
